import type { DrillKey } from '../types';

export interface DrillOutcome {
  correct: number; total: number; duration_ms: number; detail: Record<string, unknown>;
  /** 결과 화면에 덧붙일 한 판 요약 (서버로 보내지 않음) */
  summary?: string[];
}
export interface DrillProps { level: number; seed: number; onDone: (o: DrillOutcome) => void }

export const DRILL_ORDER: DrillKey[] = ['lang', 'calc', 'data', 'rule', 'seq', 'op', 'figure', 'space', 'percept', 'sjt', 'mech', 'focus', 'color', 'fluency', 'solve', 'biz', 'memory', 'calib'];

/** 성인용 직업적성검사(고용24)의 11개 적성 요인 + 기타 — 종목을 요인별로 묶는다 */
export const FACTORS = [
  { key: 'verbal', label: '언어력', test: '어휘력 · 문장독해력' },
  { key: 'numeric', label: '수리력', test: '계산능력 · 자료해석력' },
  { key: 'reasoning', label: '추리력', test: '수열추리력 · 도형추리력' },
  { key: 'spatial', label: '공간지각력', test: '조각맞추기 · 그림맞추기' },
  { key: 'perceptual', label: '사물지각력', test: '지각속도' },
  { key: 'situational', label: '상황판단력', test: '상황판단력' },
  { key: 'mechanical', label: '기계능력', test: '기계능력' },
  { key: 'focus', label: '집중력', test: '집중력' },
  { key: 'color', label: '색채지각력', test: '색혼합 · 색구분' },
  { key: 'fluency', label: '사고유창력', test: '사고유창력' },
  { key: 'solving', label: '문제해결능력', test: '문제해결능력' },
  { key: 'other', label: '기억·판단', test: '' },
] as const;
export type FactorKey = (typeof FACTORS)[number]['key'];

export const DRILL_META: Record<DrillKey, { label: string; factor: FactorKey; ability: string; why: string; how: string[] }> = {
  rule: {
    factor: 'reasoning',
    label: '규칙 간파',
    ability: '처음 보는 규칙 파악',
    why: '두뇌 게임의 첫 승부는 규칙을 누가 먼저 꿰뚫느냐입니다. 예시로 가설을 세우고, 틀리면 바로 고치는 연습입니다.',
    how: [
      '숨은 규칙에 맞는 카드(✓)와 아닌 카드(✗)를 보여 줍니다.',
      '새 카드가 규칙에 맞는지 고르세요. 정답을 알려 주면 그 카드도 예시에 쌓입니다.',
      '마지막에 규칙이 무엇이었는지 고릅니다 (2점).',
    ],
  },
  calc: {
    factor: 'numeric',
    label: '암산·확률',
    ability: '빠른 계산·경우의 수',
    why: '확률과 기댓값을 바로 셀 수 있어야 베팅·협상·투자에서 감이 아니라 숫자로 판단합니다.',
    how: [
      '10문제, 문제마다 제한 시간이 있습니다.',
      '종이 없이 머리로 계산하세요. 숫자는 입력하고, 확률은 보기에서 고릅니다.',
    ],
  },
  memory: {
    factor: 'other',
    label: '판 기억',
    ability: '판 흐름 기억',
    why: '누가 무엇을 냈는지 기억하면 남은 카드와 상대의 수를 좁힐 수 있습니다.',
    how: [
      '사람들이 카드를 한 장씩 냅니다. 한 번만 보여 줍니다.',
      '다 지나가면 누가 무엇을 냈는지 묻습니다.',
      '요령: 사람마다 합계를 굴리며 보거나, 장면을 짧은 이야기로 엮어 보세요.',
    ],
  },
  seq: {
    factor: 'reasoning',
    label: '수열 추리',
    ability: '숫자 규칙 찾기',
    why: '수열은 지능 검사에 꼭 나오는 규칙 찾기 문제입니다. 차이·비율·자리별로 쪼개 보는 습관을 들입니다.',
    how: [
      '숫자들의 규칙을 찾아 ? 자리의 수를 입력하세요. 8문제, 문제마다 제한 시간이 있습니다.',
      '틀리면 규칙을 설명해 줍니다.',
      '요령: 이웃한 수의 차이 → 몇 배인지 → 홀수·짝수 번째를 따로, 차례로 보세요.',
    ],
  },
  op: {
    factor: 'reasoning',
    label: '기호 연산',
    ability: '숨은 공식 찾기',
    why: '결과에서 계산식을 거꾸로 찾아내는 연습입니다. 수수료·점수·게임 공식이 "어떻게 계산됐나"를 알아내는 것과 같은 사고입니다.',
    how: [
      '처음 보는 기호의 예시(3 ◎ 2 = 12 …)를 보고 숨은 계산 규칙을 찾아 ? 를 채우세요. 6문제.',
      '예시는 답이 하나로 정해질 만큼 나옵니다. 틀리면 규칙을 보여 줍니다.',
      '요령: 더하기·곱하기를 먼저 대 보고, 안 맞으면 두 수를 이어 붙인 꼴(3, 2 → 32)도 의심하세요. 세운 가설은 모든 예시에 대 봅니다.',
    ],
  },
  calib: {
    factor: 'other',
    label: '확신도 맞히기',
    ability: '내 확신 보정',
    why: '투자·사업 판단은 "내가 얼마나 확실히 아는가"를 정확히 아는 데서 갈립니다. 확신을 걸고 바로 채점받는 보정 훈련은 효과가 연구로 비교적 잘 확인된 훈련입니다.',
    how: [
      '둘 중 답을 고르고, 얼마나 확신하는지(50~99%) 겁니다. 8문제.',
      '이어서 숫자 3개는 "90% 확신 범위"를 적습니다 — 10번 중 9번은 정답이 들어가게.',
      '맞힌 수보다 확신과 실제가 맞는지가 중요합니다. 80%라고 한 문제는 10개 중 8개쯤 맞아야 정상입니다.',
    ],
  },
  biz: {
    factor: 'solving',
    label: '가게 계산',
    ability: '사업 숫자 쪼개기',
    why: '장사는 손익분기·인건비·할인 효과 같은 숫자를 빨리 쪼개 보는 사람이 이깁니다. 숫자는 다 주고, 무엇을 어떤 순서로 곱하고 나눌지 연습합니다.',
    how: [
      '작은 가게의 숫자를 보고 묻는 값을 구하세요. 5문제, 문제마다 제한 시간이 있습니다.',
      '돈은 어림해도 됩니다 (±5% 안이면 정답). 사람·기계 수는 올림·내림까지 정확히.',
      '레벨 5부터 필요 없는 숫자가 섞입니다. 맞혀도 풀이를 보여 줍니다.',
    ],
  },
  lang: {
    factor: 'verbal',
    label: '언어 (어휘·독해)',
    ability: '낱말 뜻 · 글 읽기',
    why: '어휘력과 문장독해력은 언어력 검사의 두 축입니다. 낱말의 정확한 뜻을 알고, 안내문·규정을 내 상황에 정확히 대어 보는 연습입니다.',
    how: [
      '어휘 5문제(비슷한 말·반대말·뜻·사자성어)와 글 읽기 3문제가 나옵니다.',
      '글 문제는 안내문·규정을 상황에 적용하는 것과 설명하는 글입니다. 틀리면 근거가 된 줄을 보여 줍니다.',
      '요령: 기억에 기대지 말고 보기마다 글의 해당 줄로 돌아가 대조하세요.',
    ],
  },
  data: {
    factor: 'numeric',
    label: '자료 해석',
    ability: '표·그래프 계산',
    why: '수리력 검사의 자료해석은 표와 그래프에서 증가율·비중·합계를 빨리 뽑는 능력입니다. 기준(무엇 대비인가)을 헷갈리는 실수를 오답으로 넣었습니다.',
    how: [
      '표나 막대그래프를 보고 계산하거나 옳은 설명을 고르세요. 6문제.',
      '증가율 = (나중 − 처음) ÷ 처음. 비율의 평균은 사람 수로 가중해야 합니다.',
      '틀리면 계산 과정을 보여 줍니다.',
    ],
  },
  figure: {
    factor: 'reasoning',
    label: '도형 추리',
    ability: '도형 변화 규칙',
    why: '추리력 검사의 도형추리입니다. 바늘 방향·점 위치·칠하기·변의 수·막대 수 가운데 무엇이 어떻게 바뀌는지 하나씩 떼어 보는 연습입니다.',
    how: [
      '앞의 도형 4개를 보고 다음 도형을 고르세요 (레벨 8부터 3×3 빈칸 채우기도 나옵니다).',
      '속성을 하나씩 따로 보세요: 바늘 → 점 → 칠하기 → 변의 수 → 막대.',
      '오답은 정답과 속성 하나만 다릅니다.',
    ],
  },
  space: {
    factor: 'spatial',
    label: '공간 지각',
    ability: '돌리기 · 맞추기 · 접기',
    why: '공간 능력은 연습 효과가 연구로 비교적 잘 확인된 능력입니다 (연습한 과제와 비슷한 공간 과제로도 어느 정도 옮겨 감). 조각맞추기·그림맞추기 유형을 섞었습니다.',
    how: [
      '도형 회전(뒤집힌 것 구별), 조각 맞추기, 종이 접어 구멍 뚫기, 전개도 마주 보는 면이 섞여 나옵니다.',
      '회전: 모양의 튀어나온 귀퉁이 하나를 기준으로 잡고 따라가세요.',
      '종이 접기: 접은 순서의 반대로 하나씩 펼치면 구멍이 접힌 선을 기준으로 거울처럼 늘어납니다.',
    ],
  },
  percept: {
    factor: 'perceptual',
    label: '지각 속도',
    ability: '같고 다름 빨리 보기',
    why: '사물지각력은 비슷한 것들의 차이를 빠르고 정확하게 알아차리는 능력입니다. 서류 검토·숫자 대조 같은 일의 속도와 정확도에 바로 이어집니다.',
    how: [
      '두 줄이 똑같은지 다른지 고르세요. 20문제, 문제마다 몇 초만 줍니다.',
      '키보드: ← 같다, → 다르다.',
      '요령: 앞에서부터 두세 글자씩 묶어 비교하세요.',
    ],
  },
  sjt: {
    factor: 'situational',
    label: '상황 판단',
    ability: '바람직한 대처 고르기',
    why: '상황판단력은 갈등·실수·애매한 지시 같은 상황에서 더 나은 대처를 고르는 능력입니다. 원칙(일찍 알리기, 당사자와 직접·건설적으로, 정직·규칙·안전)을 익히면 빨리 좋아집니다.',
    how: [
      '상황마다 가장 바람직한 행동과 가장 바람직하지 않은 행동을 고릅니다. 4상황, 8문제.',
      '틀리면 원칙을 보여 줍니다. 정답이 하나뿐인 영역은 아니니 이유를 읽고 기준을 익히세요.',
    ],
  },
  mech: {
    factor: 'mechanical',
    label: '기계 원리',
    ability: '기어 · 지렛대 · 도르래',
    why: '기계능력은 기계와 물체가 움직이는 원리를 이해하는 능력입니다. 원리 몇 가지(맞물린 기어는 반대로, 무게 × 거리, 줄 가닥 수)만 알면 많은 문제가 풀립니다.',
    how: [
      '그림을 보고 방향·힘·회전수를 고르세요. 6문제.',
      '마찰과 도르래 무게는 무시합니다.',
      '틀리면 원리를 보여 줍니다.',
    ],
  },
  focus: {
    factor: 'focus',
    label: '집중',
    ability: '방해 속 집중 유지',
    why: '집중력 검사는 방해 자극이 있어도 한 가지에 주의를 붙잡아 두는 능력을 봅니다. 비슷한 기호 세기와 색 글자(스트룹)로 연습합니다.',
    how: [
      '기호 판에서 목표 기호의 개수를 세고, 사이사이 색 글자 문제가 나옵니다.',
      '색 글자는 글자의 뜻이 아니라 색을 고르세요. 바로 다음으로 넘어갑니다.',
    ],
  },
  color: {
    factor: 'color',
    label: '색 혼합·구분',
    ability: '색 섞기 · 미세한 색 차이',
    why: '색채지각력 검사는 두 색을 섞은 색을 떠올리는 색혼합과 비슷한 색을 가려내는 색구분으로 이뤄집니다.',
    how: [
      '물감 섞은 색 고르기와 조금 다른 칸 찾기가 번갈아 나옵니다.',
      '물감 규칙: 빨강+노랑 = 주황, 노랑+파랑 = 초록, 빨강+파랑 = 보라, 흰색은 연하게, 검정은 어둡게.',
      '레벨이 오르면 보기 색끼리의 차이가 줄어듭니다.',
    ],
  },
  fluency: {
    factor: 'fluency',
    label: '아이디어 내기',
    ability: '짧은 시간에 많이',
    why: '사고유창력은 짧은 시간에 서로 다른 아이디어를 많이 내는 능력입니다. 연습하면 개수가 늘어나는 편이고, 기획·문제 해결의 재료가 됩니다.',
    how: [
      '주제 2개, 주제마다 90초. 떠오르는 대로 한 줄씩 적고 Enter.',
      '질은 따지지 않습니다. 같은 말 반복만 하나로 셉니다.',
      '요령: 범주를 바꿔 가며 짜내세요 (재료로 · 도구로 · 놀이로 · 돈벌이로 …).',
    ],
  },
  solve: {
    factor: 'solving',
    label: '문제 해결',
    ability: '조건 정리 · 최선의 선택',
    why: '문제해결능력은 조건을 정리해 실제로 쓸 수 있는 답을 찾는 능력입니다. 일정·길·예산·작업 순서처럼 일에서 바로 쓰는 문제로 연습합니다.',
    how: [
      '회의 잡기, 길 찾기, 조건 추리, 예산 고르기, 작업 순서 가운데 5문제.',
      '요령: 조건을 표로 옮겨 적으세요. 예산은 큰 것부터 담지 말고 조합을 비교합니다.',
      '틀리면 풀이를 보여 줍니다.',
    ],
  },
};

export const elapsed = (start: number): number => Math.max(0, Math.round(performance.now() - start));
